"use client";

import { CLink } from "@/components/common/customLink/CustomLink";
import Button from "@/components/ui/button/Button";
import Image from "next/image";

interface BrandLogo {
  url: string;
  altText?: string;
}

interface GridBrand {
  _key: string;
  logo?: BrandLogo;
  link?: { url?: string };
}

interface LogoGridProps {
  brands: GridBrand[];
  viewAllLabel?: string;
  viewAllHref?: string;
  locale: string;
}

/**
 * Static grid layout — shows ALL brands in a responsive CSS grid.
 * Mirrors the Carousel/Marquee visual style (grayscale → colour on hover).
 */
export default function LogoGrid({
  brands,
  viewAllLabel,
  viewAllHref = "",
  locale,
}: LogoGridProps) {
  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-y-12 gap-x-8">
        {brands.map((brand) => {
          const logoSrc = brand.logo?.url || null;

          const content = (
            <div className="flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all duration-300 h-20 md:h-24">
              {logoSrc && (
                <div className="relative w-full h-full max-w-[120px]">
                  <Image
                    src={logoSrc}
                    alt="Brand logo"
                    fill
                    sizes="(max-width: 768px) 120px, 120px"
                    className="object-contain"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          );

          if (brand.link?.url) {
            return (
              <CLink key={brand._key} href={brand.link.url} className="group block">
                {content}
              </CLink>
            );
          }

          return <div key={brand._key}>{content}</div>;
        })}
      </div>

      {/* View All Button — only rendered when a CMS link is configured */}
      {viewAllHref && (
        <CLink
          href={viewAllHref}
          className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9"
        >
          <Button
            variant="contained"
            className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
          >
            {viewAllLabel || (locale === "ar" ? "عرض الكل" : "View All")}
          </Button>
        </CLink>
      )}
    </div>
  );
}
